import React, { useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Images } from "./components/KonvaImage";
import { getModelInfo } from "./utils/process";
import { AppShell, Burger, Group, rem } from "@mantine/core";
import { useAppStore } from "./utils/state";
import SidePanel from "./components/SidePanel";
import { useDisclosure } from "@mantine/hooks";

export default function App() {
  const {
    isLoading,
    error,
    currentModel,
    isModelSwitching,
    image,
    setIsIOS,
    setIsLoading,
    handleModelChange,
    processImage,
  } = useAppStore();

  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();

  useEffect(() => {
    // Only check iOS on load since that won't change
    const { isIOS: isIOSDevice } = getModelInfo();
    setIsIOS(isIOSDevice);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    // Handle paste event
    const handlePaste = async (event: ClipboardEvent) => {
      const clipboardItems = event?.clipboardData?.items;

      if (!clipboardItems) return;

      for (const item of clipboardItems) {
        if (item.type.startsWith("image")) {
          const file = item.getAsFile();
          if (file) {
            onDrop([file]);
            // Only process the first image
            break;
          }
        }
      }
    };
    document.addEventListener("paste", handlePaste);

    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  }, []);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;
      await processImage(acceptedFiles[0]);
    },
    [processImage]
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".mp4"],
    },
    multiple: false,
  });

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: 300,
        breakpoint: "sm",
        collapsed: { mobile: !mobileOpened, desktop: false },
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group
          h="100%"
          px="md"
          className="font-black text-2xl"
          justify="space-between"
          gap={4}
        >
          <Burger
            opened={mobileOpened}
            onClick={toggleMobile}
            hiddenFrom="sm"
            size="sm"
          />
          <div>
            <span className="mr-0.5 font-thin">[𝕥]</span>
            ImageType
          </div>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <SidePanel />
      </AppShell.Navbar>

      <AppShell.Main>
        {!image && (
          <div
            {...getRootProps()}
            className={`p-8 mb-8 max-h-[calc(100vh-80px)] h-[calc(100vh-80px)] border-2 border-dashed text-center cursor-pointer transition-colors duration-300 ease-in-out bg-white grid place-items-center
                ${isDragAccept ? "border-green-500 bg-green-50" : ""}
                ${isDragReject ? "border-red-500 bg-red-50" : ""}
                ${
                  isDragActive
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-300 hover:border-blue-500 hover:bg-blue-50"
                }
                ${isLoading || isModelSwitching ? "cursor-not-allowed" : ""}
              `}
          >
            <input
              {...getInputProps()}
              className="hidden"
              disabled={isLoading || isModelSwitching}
            />
            <div className="flex flex-col items-center gap-2">
              {isLoading || isModelSwitching ? (
                <>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="animate-spin mr-2 text-blue-600 inline-block"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    width="24"
                    height="24"
                    strokeWidth="2"
                  >
                    <path d="M12 3a9 9 0 1 0 9 9"></path>
                  </svg>
                  <p className="text-lg text-gray-600">
                    {isModelSwitching
                      ? "Switching models..."
                      : "Loading background removal model..."}
                  </p>
                </>
              ) : error ? (
                <>
                  <svg
                    className="w-12 h-12 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                  <p className="text-lg text-red-600 font-medium mb-2">
                    {error.message}
                  </p>
                  {currentModel === "Xenova/modnet" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleModelChange({
                          target: { value: "briaai/RMBG-1.4" },
                        } as any);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      Switch to Cross-browser Version
                    </button>
                  )}
                </>
              ) : (
                <>
                  <svg
                    className="w-12 h-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                    />
                  </svg>
                  <p className="text-lg text-gray-600">
                    {isDragActive
                      ? "Drop your image here..."
                      : "Drag and drop image here"}
                  </p>
                  <p className="text-sm text-gray-500">
                    or click to select files
                  </p>
                </>
              )}
            </div>
          </div>
        )}
        {image && <Images image={image} />}
      </AppShell.Main>
    </AppShell>
  );
}
